import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { get, chunk } from 'lodash';

import {
  Button,
  ButtonsWrapper,
  NOTIFICATION_TYPE,
  showNotification,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';

import { useGetAllEmployeesForList } from 'src/hooks/employees/get-all-employees-for-list.hook';
import { useGetDocumentImporterTemplate } from 'src/hooks/document-importer-template/get-document-importer-template-by-id.hook';
import { useDeleteDocumentImporterById } from 'src/hooks/document-importer/delete-document-importer-by-id.hook';
import { useUploadDocumentImporterFiles } from 'src/hooks/document-importer/upload-document-importer-files.hook';
import { useCreateDocumentImporter } from 'src/hooks/document-importer/create-document-importer.hook';

import { removeFileExtension } from 'src/common/helpers/common.helpers';
import { DocumentImporterPreviewTable } from '../document-importer-preview-table';

import { SKIP_LABEL } from '../../document-importer-list.constants';
import { HeaderContainer } from '../../document-importer-list.styles';

import {
  IDocumentImporterItem,
  IDocumentImporterPreview,
  IFileListItem,
} from '../../document-importer-list.interfaces';

const CreateDocumentImporter = ({
  document,
  currentCompanyId,
  onClose,
}: {
  document: IDocumentImporterItem;
  currentCompanyId?: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation(['common', 'documentImporter']);
  const [files, setFiles] = useState<IFileListItem[] | []>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const {
    title,
    importedDocuments = {},
    documentImporterTemplateId,
    documentLabelId,
    description,
  } = document;

  const { documentImporterTemplate = {} } = useGetDocumentImporterTemplate(
    documentImporterTemplateId
  );

  const handleLoadingStart = () => {
    setLoading(true);
  };

  const handleLoadingClose = () => {
    setLoading(true);
  };

  const { separator, valueEmployeeId } = documentImporterTemplate;

  const { employees, loading: employeeLoading } = useGetAllEmployeesForList({
    filter: { companyId: currentCompanyId, accountActive: true },
  });

  const deleteDocumentImporterById = useDeleteDocumentImporterById();

  const { uploadDocumentImporterFiles } = useUploadDocumentImporterFiles();

  const { createDocumentImporter } = useCreateDocumentImporter();

  const employeesOptions = [
    SKIP_LABEL,
    ...employees?.map((el: any) => ({
      label: `${el?.profile?.displayName} (${el?.employeePersonalInfo?.employeeNumber})`,
      value: el?._id,
    })),
  ];

  useEffect(() => {
    const listData = importedDocuments?.fileList?.map(file => {
      const newFileName = removeFileExtension(file.name);
      const fileArray = newFileName.split(separator);
      const fileEmployeeId = fileArray[Number(valueEmployeeId)];
      const employee = employees.find(
        (empl: any) => Number(fileEmployeeId) === Number(empl?.employeePersonalInfo?.employeeNumber)
      );
      return {
        ...file,
        memberId: employee?._id,
      };
    });
    setFiles(listData || []);
  }, [document, employees, valueEmployeeId, importedDocuments?.fileList, separator]);

  const handleEmployeeChange = (file: IDocumentImporterPreview) => (value: string) => {
    const fileToReplaceIndex = files.findIndex(el => el?.uid === file.uid);
    const fileList = [...files];
    fileList.splice(fileToReplaceIndex, 1, { ...file, memberId: value });
    setFiles(fileList);
  };

  const handleImportStart = (fileList: IFileListItem[]) => async () => {
    const documentImporterItems: {
      file?: IFileListItem;
      memberId: string;
      fileName: string;
    }[] = [];

    fileList.forEach(el => {
      if (el?.memberId && el.memberId !== SKIP_LABEL.value) {
        documentImporterItems.push({
          file: el?.originFileObj,
          memberId: el.memberId,
          fileName: el?.name,
        });
      }
    });

    const filesToUpload = chunk(documentImporterItems, 5);

    try {
      handleLoadingStart();
      const result = await createDocumentImporter({
        variables: {
          payload: {
            title: title,
            companyId: currentCompanyId,
            documentImporterTemplateId,
            documentLabelId,
            description,
          },
        },
      });

      const documentImporterId = get(result, 'data.createDocumentImporter._id');

      if (documentImporterId) {
        try {
          for (const file of filesToUpload) {
            await uploadDocumentImporterFiles({
              variables: {
                payload: {
                  documentImporterId,
                  files: file,
                },
              },
            });
          }
          showNotification({
            isIcon: false,
            type: NOTIFICATION_TYPE.success,
            message: t('documentImporter:notifications.created'),
          });
          onClose();
          handleLoadingClose();
        } catch (error) {
          await deleteDocumentImporterById({
            variables: { id: documentImporterId },
          });
          handleLoadingClose();
        }
      }
    } catch (error) {
      console.error(error);
      handleLoadingClose();
    } finally {
      handleLoadingClose();
    }
  };

  return (
    <Fragment>
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t(`documentImporter:labels.preview`, {
            name: title,
          })}
        </Typography>
        <ButtonsWrapper marginTop={16}>
          <Button onClick={handleImportStart(files)} disabled={loading} type="primary">
            {t('common:buttons.start')}
          </Button>
          <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </HeaderContainer>
      <DocumentImporterPreviewTable
        loading={employeeLoading}
        employeesOptions={employeesOptions}
        handleEmployeeChange={handleEmployeeChange}
        dataSource={files}
      />
    </Fragment>
  );
};

export default CreateDocumentImporter;
